import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import secureStorage from '../utils/secureStorage';
import { API_BASE_URL } from '../config';
import { writeSanitizedHTML } from '../utils/safePrint';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { X, Send, Bot, User as UserIcon, Trash2, Shield, Loader2, Printer } from 'lucide-react';

const API = `${API_BASE_URL}/api`;

/**
 * AIAssistantModal — Claude Sonnet 4.5 chat scoped to Canadian ICS /
 * emergency preparedness. Backend endpoint keeps a per-(exercise, user)
 * session so conversations persist across opens.
 */
const AIAssistantModal = ({ open, onClose, currentExercise, currentView = '' }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const scrollRef = useRef(null);

  const authHeaders = () => {
    const token = secureStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadHistory = useCallback(async () => {
    if (!currentExercise?.id) {
      setMessages([]);
      return;
    }
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/ai/sessions/${currentExercise.id}`, { headers: authHeaders() });
      setMessages(res.data?.messages || []);
    } catch (e) {
      // 404 is normal on first open — no prior session yet
      if (e.response?.status !== 404) {
        console.warn('AI history load failed', e);
      }
      setMessages([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [currentExercise?.id]);

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    if (!currentExercise?.id) {
      toast.error('Select an exercise first — the assistant works in the context of your active exercise.');
      return;
    }
    const nextMessages = [...messages, { role: 'user', content: text, ts: new Date().toISOString() }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/ai/chat`,
        {
          exercise_id: currentExercise.id,
          message: text,
          current_view: currentView || 'Settings › AI Assistant',
        },
        { headers: authHeaders() }
      );
      const reply = res.data?.reply || 'No reply received.';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply, ts: new Date().toISOString() }]);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(detail || 'AI request failed');
      setMessages((prev) => [...prev, { role: 'assistant', content: `⚠️ ${detail || 'Request failed'}`, ts: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const clearSession = async () => {
    if (!currentExercise?.id) return;
    if (!window.confirm('Clear this conversation? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/ai/sessions/${currentExercise.id}`, { headers: authHeaders() });
      setMessages([]);
      toast.success('Conversation cleared');
    } catch (e) {
      toast.error('Failed to clear conversation');
    }
  };

  const escapeHtml = (str = '') =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const formatTs = (ts) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return '';
    }
  };

  const printConversation = (msgList) => {
    const list = msgList && msgList.length ? msgList : messages;
    if (!list.length) {
      toast.error('There is no conversation to print yet.');
      return;
    }
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print the conversation.');
      return;
    }
    const exerciseName = escapeHtml(currentExercise?.exercise_name || 'No exercise');
    const rows = list
      .map((m) => {
        const isUser = m.role === 'user';
        const author = isUser ? 'You' : 'AI Deputy IC (Claude Sonnet 4.5)';
        return `
          <div class="msg ${isUser ? 'user' : 'assistant'}">
            <div class="msg-header">
              <span class="author">${escapeHtml(author)}</span>
              <span class="ts">${escapeHtml(formatTs(m.ts || m.timestamp))}</span>
            </div>
            <div class="msg-body">${escapeHtml(m.content || '')}</div>
          </div>
        `;
      })
      .join('');

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>AI Deputy IC Conversation — ${exerciseName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #222; }
            h1 { color: #ea580c; text-align: center; border-bottom: 3px solid #ea580c; padding-bottom: 10px; margin-bottom: 6px; }
            .subtitle { text-align: center; color: #666; font-size: 13px; margin-bottom: 24px; }
            .msg { margin: 14px 0; padding: 12px 14px; border-radius: 6px; page-break-inside: avoid; border-left: 4px solid #ccc; background: #fafafa; }
            .msg.user { border-left-color: #2563eb; background: #eff6ff; }
            .msg.assistant { border-left-color: #ea580c; background: #fff7ed; }
            .msg-header { display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 6px; color: #555; }
            .author { font-weight: 600; color: #333; }
            .msg-body { white-space: pre-wrap; font-size: 14px; line-height: 1.5; }
            .footer { margin-top: 32px; text-align: center; color: #999; font-size: 11px; border-top: 1px solid #eee; padding-top: 10px; }
            @media print { body { padding: 20px; } .msg { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>AI Deputy IC Conversation</h1>
          <div class="subtitle">
            Exercise: <strong>${exerciseName}</strong> · Generated ${escapeHtml(new Date().toLocaleString())}
          </div>
          ${rows}
          <div class="footer">
            EXRSIM — AI guidance is generated by Claude Sonnet 4.5. Verify against official doctrine before acting.
          </div>
        </body>
      </html>
    `;
    writeSanitizedHTML(printWindow.document, html);
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!open) return null;

  const suggestions = [
    'Summarise the current situation for a new arrival at Command',
    'What ICS 200/300 procedures apply to a wildland urban interface fire?',
    'Draft an initial ICS 201 briefing for this exercise',
    'What Canadian regulations govern mandatory evacuations?',
    'Recommend Section Chiefs given the objectives on file',
  ];

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/70 p-3">
      <Card className="bg-gray-900 border-orange-500/40 w-full max-w-3xl h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-orange-500/15 border border-orange-500/40 flex items-center justify-center">
              <Shield className="h-5 w-5 text-orange-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-orange-500 font-semibold text-lg leading-tight">AI Deputy — Canadian ICS Advisor</h2>
              <p className="text-gray-500 text-xs truncate">
                Claude Sonnet 4.5 · {currentExercise?.exercise_name || 'No exercise selected'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={printConversation}
                className="text-gray-400 hover:text-orange-400 p-2"
                aria-label="Print conversation"
                data-testid="ai-print-btn"
                title="Print conversation"
              >
                <Printer className="h-4 w-4" />
              </button>
            )}
            {messages.length > 0 && (
              <button
                onClick={clearSession}
                className="text-gray-400 hover:text-red-400 p-2"
                aria-label="Clear conversation"
                data-testid="ai-clear-btn"
                title="Clear conversation"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-orange-400 p-2"
              aria-label="Close"
              data-testid="ai-close-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {!currentExercise?.id ? (
          <div className="flex-1 flex items-center justify-center p-6 text-center">
            <div>
              <Shield className="h-10 w-10 mx-auto text-gray-600 mb-2" />
              <p className="text-gray-300">Select an exercise first.</p>
              <p className="text-gray-500 text-sm mt-1">
                The AI advisor works in the context of your active exercise (HIRA, objectives, positions, participants).
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-messages">
              {loadingHistory && (
                <div className="text-center text-gray-500 text-sm py-6">Loading conversation…</div>
              )}
              {!loadingHistory && messages.length === 0 && (
                <div className="space-y-3">
                  <p className="text-gray-300 text-sm">
                    Ask questions grounded in Canadian ICS doctrine, NFPA / CSA standards, and provincial emergency
                    procedures. The advisor sees your exercise context (HIRA, objectives, positions).
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setInput(s)}
                        className="text-left text-sm text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg p-2"
                        data-testid={`ai-suggestion-${i}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center border ${
                    m.role === 'user'
                      ? 'bg-blue-500/15 border-blue-500/40'
                      : 'bg-orange-500/15 border-orange-500/40'
                  }`}>
                    {m.role === 'user' ? (
                      <UserIcon className="h-4 w-4 text-blue-400" />
                    ) : (
                      <Bot className="h-4 w-4 text-orange-400" />
                    )}
                  </div>
                  <div className="flex flex-col max-w-[80%]">
                    <div className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      m.role === 'user'
                        ? 'bg-blue-500/20 border border-blue-500/30 text-white'
                        : 'bg-gray-800 border border-gray-700 text-gray-100'
                    }`}>
                      {m.content}
                    </div>
                    {m.role === 'assistant' && (
                      <button
                        type="button"
                        onClick={() => {
                          // Print the immediately-preceding user prompt paired with this reply
                          let pair = [];
                          for (let j = i - 1; j >= 0; j--) {
                            if (messages[j].role === 'user') { pair.push(messages[j]); break; }
                          }
                          pair.push(m);
                          printConversation(pair);
                        }}
                        data-testid={`ai-print-message-${i}`}
                        title="Print this Q&A"
                        className="mt-1 self-start inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-orange-400 transition-colors"
                      >
                        <Printer className="h-3 w-3" />
                        Print this Q&amp;A
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex gap-2 items-center text-gray-400 text-sm">
                  <div className="w-8 h-8 rounded-full bg-orange-500/15 border border-orange-500/40 flex items-center justify-center">
                    <Loader2 className="h-4 w-4 text-orange-400 animate-spin" />
                  </div>
                  Thinking…
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="p-3 border-t border-gray-700">
              <div className="flex items-end gap-2">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Ask about ICS forms, Canadian regulations, procedures, or your current exercise… (Enter to send, Shift+Enter for newline)"
                  className="bg-gray-800 border-gray-700 text-white flex-1 min-h-[48px] max-h-40"
                  data-testid="ai-input"
                  disabled={sending}
                />
                <Button
                  onClick={send}
                  disabled={sending || !input.trim()}
                  className="bg-orange-500 hover:bg-orange-600 text-black font-semibold min-h-[48px]"
                  data-testid="ai-send-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-[10px] text-gray-600 mt-1">
                Advice is generated by Claude Sonnet 4.5. Verify against official doctrine before acting.
              </p>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default AIAssistantModal;
